import React, { useState } from 'react';
import GenericTable from 'shared/components/common/table/table';
import styles from './style.module.scss';
import { DelIcon, EditIcon, PlusIcon, ProjectNameIcon, StatusCheckIcon } from 'assets';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Heading from 'shared/components/common/heading/Heading';
import Switch2 from 'shared/components/common/switch2/Switch2';
import { isRestrictActions } from 'shared/utils/Helper';
import { screenActionEnum, screenEnum } from 'shared/utils/Enum';
import DeleteConfirmation from 'shared/modal/delete_confirmation/DeleteConfirmation';
import { toast } from 'react-toastify';

const InnerRlTable = ({
    PoList,
    OffPage,
    setIsEdit,
    handleDelete,
    onRowSelect,
    onRowEdit,
    isAdminReview,
    handleApproveItem,
    handleDeclineItem,
    isLocked,
    approvingIndex,
    onApplyBatchFields,
    onAddGrandSku
}) => {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    const [selectDelUser, setSelectDelUser] = useState(null);
    const [openDelConfirm, setOpenDelConfirm] = useState(false);

    const closeDelConfirmation = () => setOpenDelConfirm(false);

    const confirmDelete = () => {
        if (selectDelUser !== null) {
            handleDelete(selectDelUser);  // Call parent delete handler
            closeDelConfirmation();
        }
    };

    const handleCopySku = (e, sku) => {
        e.stopPropagation(); // Stop row-click navigation
        if (!sku || sku === 'N/A') return;
        navigator.clipboard.writeText(sku);
        toast.success(`SKU Copied: ${sku}`);
    };

    const subTotal = PoList?.reduce((sum, item) => {
        const total = (item.price || 0) * (item.quantity || 0);
        return sum + total;
    }, 0);

 const formatPrice = (price) => {
        const num = Number(price);
        if (isNaN(num)) return 'Rs 0.00';
        return `Rs ${num.toFixed(2)}`;
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        SKU
                    </div>
                ),
                accessor: 'sku',
                minWidth: 220,
                width: 230,
                Cell: ({ row }) => {
                    const sku = row.original?.sku;
                    if (!sku) return <div>N/A</div>;
                    const isGrandSku = /^wt\d{6}[a-z][0-9]$/i.test(sku);
                    return (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: isGrandSku ? '10px' : '0px', whiteSpace: 'nowrap' }}>
                            {isGrandSku && (
                                <span
                                    style={{
                                        color: '#0284c7',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        background: '#e0f2fe',
                                        border: '1px solid #bae6fd',
                                        borderRadius: '4px',
                                        padding: '1px 5px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                    title="Grand/Sub Child SKU"
                                >
                                    ↳ Sub
                                </span>
                            )}
                            <div
                                onClick={(e) => handleCopySku(e, sku)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    background: isGrandSku ? '#f0fdf4' : '#f1f5f9',
                                    border: isGrandSku ? '1px solid #86efac' : '1px solid #cbd5e1',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: isGrandSku ? '#166534' : '#0f172a',
                                    fontWeight: '600',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    transition: 'all 0.2s ease'
                                }}
                                title="Click to copy SKU"
                                onMouseEnter={(e) => {
                                    e.target.style.background = isGrandSku ? '#dcfce7' : '#e2e8f0';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = isGrandSku ? '#f0fdf4' : '#f1f5f9';
                                }}
                            >
                                📋 {sku}
                            </div>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Compatible Brand
                    </div>
                ),
                accessor: 'Compatible',
                Cell: ({ row }) => <div>{row.original?.competebleBrand?.name}</div>,
                width: 150
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Product Model
                    </div>
                ),
                accessor: 'as',
                Cell: ({ row }) => <div>{row.original?.product?.name}</div>,
                width: 150
            },

            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Product Sub Categ.
                    </div>
                ),
                accessor: 'subCategory',
                Cell: ({ row }) => <div>{row.original?.sub_category?.name}</div>,
                width: 160
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Product Category
                    </div>
                ),
                accessor: 'categorys',
                Cell: ({ row }) => <div>{row.original?.product_category?.name}</div>,
                width: 150
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Brand
                    </div>
                ),
                accessor: 'brands',
                Cell: ({ row }) => <div>{row.original.brand?.name}</div>,
                width: 130
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Color
                    </div>
                ),
                accessor: 'color',
                Cell: ({ row }) => <div>{row.original.color}</div>,
                width: 110
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Images
                    </div>
                ),
                accessor: 'raw_images',
                Cell: ({ row }) => {
                    const images = row.original?.raw_images || [];
                    if (images.length === 0) {
                        return <span style={{ fontSize: '12px', color: '#94a3b8' }}>—</span>;
                    }
                    return (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '160px' }}>
                            {images.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img
                                        src={url}
                                        alt={`img-${i}`}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </a>
                            ))}
                        </div>
                    );
                },
                width: 160
            },
            ...(false ? [{
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Quantity
                    </div>
                ),
                accessor: 'quantity',
                Cell: ({ row }) => <div>{(row.original.quantity)}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Price
                    </div>
                ),
                accessor: 'price',
                Cell: ({ row }) => <div>{formatPrice(row.original.price)}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Total
                    </div>
                ),
                accessor: 'total',
                Cell: ({ row }) => <div>{formatPrice(row.original.price * row.original.quantity)}</div>
            }] : []),
            ...(isAdminReview ? [{
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Review
                    </div>
                ),
                accessor: 'review_status',
                Cell: ({ row }) => {
                    const status = row.original?.status || 'pending';
                    if (status === 'approved') {
                        return (
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-success text-white px-2 py-1">Approved</span>
                                {row.original?.task_created && (
                                    <span className="text-success fw-bold ms-1" title="Task Created">✓ Task Created</span>
                                )}
                            </div>
                        );
                    }
                    if (status === 'declined') {
                        return <span className="badge bg-danger text-white px-2 py-1">Declined</span>;
                    }
                    if (status === 'existing') {
                        return (
                            <span
                                className="badge px-2 py-1 fw-semibold"
                                style={{
                                    background: '#f1f5f9',
                                    color: '#64748b',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '12px',
                                    borderRadius: '6px'
                                }}
                            >
                                📦 Existing SKU
                            </span>
                        );
                    }
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-success py-1 px-2 fw-bold text-white d-flex align-items-center gap-1"
                                style={{ fontSize: '12px', border: 'none' }}
                                disabled={row.original.globalIndex === approvingIndex}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveItem(row.original.globalIndex);
                                }}
                            >
                                {row.original.globalIndex === approvingIndex ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                                        <span>Checking...</span>
                                    </>
                                ) : (
                                    "Approve"
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger py-1 px-2 fw-bold text-white"
                                style={{ fontSize: '12px', border: 'none' }}
                                disabled={row.original.globalIndex === approvingIndex}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeclineItem(row.original.globalIndex);
                                }}
                            >
                                Decline
                            </button>
                        </div>
                    );
                },
                width: 170
            }] : []),
            ...(!isLocked ? [{
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'status',
                Cell: ({ row }) => {
                    const sku = row.original?.sku;
                    const isChildSku = sku && /^wt\d{6}[a-z]$/i.test(sku);
                    return (
                        <div className={classNames(styles.Statuses)} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {isChildSku && onAddGrandSku && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddGrandSku(row.original);
                                    }}
                                    style={{
                                        border: '1px solid #0284c7',
                                        background: '#f0f9ff',
                                        color: '#0284c7',
                                        borderRadius: '6px',
                                        padding: '2px 7px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#0284c7';
                                        e.currentTarget.style.color = '#ffffff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#f0f9ff';
                                        e.currentTarget.style.color = '#0284c7';
                                    }}
                                    title={`Create Grand/Sub-SKU (0-9) for ${sku}`}
                                >
                                    + Sub-SKU
                                </button>
                            )}
                            <EditIcon
                                className="pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onRowEdit) {
                                        onRowEdit(row.original);
                                    }
                                }}
                            />
                            <DelIcon
                                className="pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isRestrictActions(screenEnum.rough_listing, screenActionEnum.delete)) {
                                        setOpenDelConfirm(true);
                                        setSelectDelUser(row.original.globalIndex);
                                    }
                                }}
                            />
                        </div>
                    );
                },
                minWidth: 150,
                width: 160
            }] : [])
        ],

        [theme, isAdminReview, handleApproveItem, handleDeclineItem, onRowEdit, isLocked, approvingIndex, onAddGrandSku]
    );
    const groupedBadges = React.useMemo(() => {
        if (!PoList || PoList.length === 0) return [];
        const groups = {};
        PoList.forEach((item, idx) => {
            const b = item.badge || 1;
            if (!groups[b]) {
                groups[b] = {
                    badgeNumber: b,
                    items: [],
                    representativeImage: null
                };
            }
            groups[b].items.push({ ...item, globalIndex: idx });
            if (!groups[b].representativeImage && item.raw_images && item.raw_images.length > 0) {
                groups[b].representativeImage = item.raw_images[0];
            }
        });

        // For each group, find the best "source" item (most fields filled)
        // and determine if checkbox should be shown
        const result = Object.values(groups).sort((a, b) => a.badgeNumber - b.badgeNumber);
        result.forEach(group => {
            const countFilledFields = (item) => {
                let score = 0;
                if (item.brand) score += 2;
                if (item.product_category) score += 2;
                if (item.product) score += 2;
                if (item.sub_category) score += 2;
                if (item.competebleBrand || item.compatiableBrand) score += 2;
                return score;
            };

            const filledItems = group.items.filter(item => countFilledFields(item) > 0);

            // Sort by score descending to get the most complete item as source
            const sortedByCompleteness = [...filledItems].sort(
                (a, b) => countFilledFields(b) - countFilledFields(a)
            );

            group.sourceItem = sortedByCompleteness[0] || null;

            // Show checkbox if batch has more than 1 item and at least one item has filled fields
            group.showApplyCheckbox = group.items.length > 1 && !!group.sourceItem;

            // Organize items so each child variant is immediately followed by its grand child variants (0-9)
            const parentItems = [];
            const grandItemsMap = {}; // { "wt000550a": [item1, item2] }
            const otherItems = [];

            group.items.forEach(item => {
                const sku = item.sku ? item.sku.trim() : "";
                const isGrand = /^wt\d{6}[a-z][0-9]$/i.test(sku) || item.parentChildSku;
                if (isGrand) {
                    const parentKey = (item.parentChildSku || sku.slice(0, -1)).toLowerCase();
                    if (!grandItemsMap[parentKey]) grandItemsMap[parentKey] = [];
                    grandItemsMap[parentKey].push(item);
                } else if (sku && /^wt\d{6}[a-z]$/i.test(sku)) {
                    parentItems.push(item);
                } else {
                    otherItems.push(item);
                }
            });

            // Sort parent items alphabetically by SKU (e.g. a, b, c...)
            parentItems.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));

            const orderedItems = [];
            parentItems.forEach(parent => {
                orderedItems.push(parent);
                const parentKey = (parent.sku || "").toLowerCase();
                const grandChildren = grandItemsMap[parentKey] || [];
                // Sort grandchildren by their numeric suffix (0, 1, 2...)
                grandChildren.sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));
                orderedItems.push(...grandChildren);
                delete grandItemsMap[parentKey];
            });

            // Any remaining grand items (if parent wasn't in the list)
            Object.values(grandItemsMap).forEach(remainingList => {
                orderedItems.push(...remainingList);
            });

            // Any other/unassigned items
            orderedItems.push(...otherItems);

            group.items = orderedItems;
        });

        return result;
    }, [PoList]);

    return (
        <div className={'text-start'}>
            <div className={styles.Header}>
                <Heading heading={'Rough Listing'} headingStyle={styles.Heading} />
            </div>

            {groupedBadges.map((badge) => (
                <div 
                    key={badge.badgeNumber} 
                    style={{
                        background: '#f8fafc',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        padding: '24px',
                        marginBottom: '24px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)'
                    }}
                >
                    {/* Batch Info Header */}
                    <div className="d-flex align-items-center mb-3 gap-3" style={{ flexWrap: 'wrap' }}>
                        {badge.representativeImage ? (
                            <img 
                                src={badge.representativeImage} 
                                alt={`Batch ${badge.badgeNumber}`} 
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '10px',
                                    objectFit: 'cover',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.08)'
                                }}
                            />
                        ) : (
                            <div 
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '10px',
                                    background: '#e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px',
                                    color: '#94a3b8'
                                }}
                            >
                                📦
                            </div>
                        )}
                        <div>
                            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '18px' }}>Batch {badge.badgeNumber}</h5>
                            <span className="text-muted" style={{ fontSize: '13px' }}>{badge.items.length} items in this batch</span>
                        </div>

                        {/* Apply to batch checkbox — sirf tab jab zaroorat ho */}
                        {badge.showApplyCheckbox && onApplyBatchFields && (
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginLeft: 'auto',
                                    cursor: 'pointer',
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #3b82f6',
                                    background: '#eff6ff',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#1d4ed8',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }}
                                    onChange={(e) => {
                                        if (e.target.checked && badge.sourceItem) {
                                            onApplyBatchFields(badge.badgeNumber, badge.sourceItem);
                                            e.target.checked = false; // Reset after apply
                                        }
                                    }}
                                />
                                Apply to all items in batch
                            </label>
                        )}
                    </div>

                    <GenericTable
                        columns={columns}
                        innerpo={'innerpoOn'}
                        OffPage={OffPage}
                        data={badge.items}
                        TableWrapperStyle={classNames(styles.Table)}
                        onRowClick={onRowSelect}
                    />
                </div>
            ))}

            <div className={styles.TableFooter}>
                <div className={styles.SubtotalRow}>
                    <strong>Subtotal:</strong> {subTotal.toLocaleString()}
                </div>
            </div>

            <DeleteConfirmation
                show={openDelConfirm}
                handleClose={closeDelConfirmation}
                DeleteHandler={() => {
                    confirmDelete();
                }}
                ConfimationHeading="Delete Item"
                Desc={'Are you sure you want to delete the Item?'} />

        </div>
    );
};
export default InnerRlTable;
